import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Clock, Loader2, BarChart3, PieChart, Table as TableIcon, CreditCard } from 'lucide-react'
import { AreaChart, Area, BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

type TimePeriod = 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'THIS_MONTH'
const COLORS = ['#f97316', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444']

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<TimePeriod>('LAST_7_DAYS')

  useEffect(() => {
    fetchAnalytics()
  }, [period])

  const fetchAnalytics = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/analytics/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) setAnalytics(await response.json())
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-orange-500 mx-auto mb-4" size={48} />
          <p className="text-gray-500 dark:text-white/40">Caricamento analytics...</p>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400 dark:text-white/30" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Nessun dato disponibile</h2>
          <p className="text-gray-500 dark:text-white/40">I dati appariranno quando avrai degli ordini</p>
        </div>
      </div>
    )
  }

  const statusData = [
    { name: 'In attesa', value: analytics.orderStatusDistribution.pending, color: '#f59e0b' },
    { name: 'Preparazione', value: analytics.orderStatusDistribution.preparing, color: '#3b82f6' },
    { name: 'Pronto', value: analytics.orderStatusDistribution.ready, color: '#8b5cf6' },
    { name: 'Completato', value: analytics.orderStatusDistribution.completed, color: '#10b981' },
    { name: 'Annullato', value: analytics.orderStatusDistribution.cancelled, color: '#ef4444' }
  ]

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-gray-500 dark:text-white/40 text-sm mt-1">Performance del tuo ristorante</p>
        </div>
        <div className="flex gap-1.5 bg-gray-100 dark:bg-white/[0.06] rounded-xl p-1.5 border border-gray-200 dark:border-white/[0.08]">
          {(['TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'THIS_MONTH'] as TimePeriod[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                period === p
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                  : 'text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/[0.06]'
              }`}
            >
              {p === 'TODAY' ? 'Oggi' : p === 'LAST_7_DAYS' ? '7 giorni' : p === 'LAST_30_DAYS' ? '30 giorni' : 'Mese'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: DollarSign, label: 'Ricavi', value: `€${analytics.totalRevenue.toFixed(2)}`, growth: analytics.revenueGrowth, color: 'emerald' },
          { icon: ShoppingCart, label: 'Ordini', value: analytics.totalOrders, growth: analytics.ordersGrowth, color: 'blue' },
          { icon: BarChart3, label: 'Valore Medio', value: `€${analytics.averageOrderValue.toFixed(2)}`, growth: analytics.averageOrderGrowth, color: 'violet' },
          { icon: Users, label: 'Clienti', value: analytics.activeCustomers, growth: analytics.customersGrowth, color: 'orange' }
        ].map((item, i) => {
          const colorClasses = {
            emerald: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
            blue: 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
            violet: 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400',
            orange: 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
          }
          return (
            <div key={i} className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.12] transition-all duration-200">
              <div className="flex justify-between items-start mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[item.color as keyof typeof colorClasses]}`}>
                  <item.icon size={24} />
                </div>
                <div className={`flex items-center gap-1 text-sm font-semibold ${item.growth >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {item.growth >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                  {Math.abs(item.growth)}%
                </div>
              </div>
              <h3 className="text-gray-500 dark:text-white/40 text-sm font-medium mb-1">{item.label}</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.value}</p>
            </div>
          )
        })}
      </div>

      {/* Revenue Chart */}
      <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08] mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Andamento Ricavi</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={analytics.revenueOverTime}>
            <defs>
              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-white/[0.06]" />
            <XAxis dataKey="date" stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <YAxis stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                border: '1px solid var(--tooltip-border, #e5e7eb)',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
              }}
            />
            <Area type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} fill="url(#colorRev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Orders Chart */}
      <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08] mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Andamento Ordini</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={analytics.ordersOverTime}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-white/[0.06]" />
            <XAxis dataKey="date" stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <YAxis stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                border: '1px solid var(--tooltip-border, #e5e7eb)',
                borderRadius: '12px'
              }}
            />
            <Legend />
            <Bar dataKey="completedOrders" fill="#10b981" name="Completati" radius={[6,6,0,0]} />
            <Bar dataKey="pendingOrders" fill="#f59e0b" name="In attesa" radius={[6,6,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Two Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Products */}
        <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            </div>
            Top Prodotti
          </h2>
          <div className="space-y-3">
            {analytics.topProducts.slice(0,5).map((p:any, i:number) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-orange-500/25">
                  #{i+1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">{p.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-white/40">{p.categoryName}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900 dark:text-white">€{p.revenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 dark:text-white/40">{p.quantitySold} venduti</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Distribution */}
        <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
              <PieChart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            Distribuzione Ordini
          </h2>
          <ResponsiveContainer width="100%" height={220}>
            <RePieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}
                outerRadius={80}
                dataKey="value"
              >
                {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
            </RePieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Peak Hours */}
      <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08] mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
            <Clock className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          </div>
          Orari di Punta
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={analytics.peakHours.sort((a:any,b:any) => a.hour-b.hour)}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-white/[0.06]" />
            <XAxis dataKey="timeLabel" stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <YAxis stroke="currentColor" className="text-gray-500 dark:text-white/40" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                border: '1px solid var(--tooltip-border, #e5e7eb)',
                borderRadius: '12px'
              }}
            />
            <Bar dataKey="orderCount" fill="#f97316" name="Ordini" radius={[6,6,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tables and Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Table Performance */}
        <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <TableIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            Performance Tavoli
          </h2>
          <div className="space-y-3">
            {analytics.tablePerformance.slice(0,5).map((t:any) => (
              <div key={t.tableId} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{t.tableName}</p>
                  <p className="text-xs text-gray-500 dark:text-white/40">{t.sessionCount} sessioni</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900 dark:text-white">€{t.totalRevenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 dark:text-white/40">€{t.averageRevenue.toFixed(2)} media</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            Pagamenti
          </h2>
          <div className="space-y-4">
            {analytics.paymentMethods.map((m:any, i:number) => (
              <div key={i} className="p-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold text-gray-900 dark:text-white">{m.method}</span>
                  <span className="font-bold text-gray-900 dark:text-white">€{m.totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 dark:bg-white/[0.1] rounded-full h-2">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{width: `${m.percentage}%`, backgroundColor: COLORS[i % COLORS.length]}}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-white/70 w-12 text-right">
                    {m.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Customer Behavior */}
      <div className="bg-white dark:bg-[#25252d] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.08]">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
            <Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          </div>
          Comportamento Clienti
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            {label: 'Totali', value: analytics.customerBehavior.totalCustomers, color: 'text-gray-900 dark:text-white'},
            {label: 'Di Ritorno', value: analytics.customerBehavior.returningCustomers, color: 'text-emerald-600 dark:text-emerald-400'},
            {label: 'Nuovi', value: analytics.customerBehavior.newCustomers, color: 'text-blue-600 dark:text-blue-400'},
            {label: 'Ordini Medi', value: analytics.customerBehavior.averageOrdersPerCustomer.toFixed(1), color: 'text-violet-600 dark:text-violet-400'},
            {label: 'Spesa Media', value: `€${analytics.customerBehavior.averageSpendPerCustomer.toFixed(2)}`, color: 'text-orange-600 dark:text-orange-400'},
            {label: 'Retention', value: `${analytics.customerBehavior.customerRetentionRate.toFixed(1)}%`, color: 'text-pink-600 dark:text-pink-400'}
          ].map((stat, i) => (
            <div key={i} className="text-center p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-gray-500 dark:text-white/40 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
